import axios from 'axios';
import { APIV2 } from '../../../backend';
import { useAppContext } from '../../../hooks/context/AppContext';
import { useState, useMemo } from 'react';
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";
import { version } from 'os';


// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

// Milestone interfaces
export interface MilestonePayload {
  Product_Lock: string;
  Product_Lock_Date: string;
  BCSignOff_CP: string;
  BCSignOff_CP_Date: string;
  MPTProductValidation: string;
  MPTProductValidation_Date: string;
  FirstProduction: string;
  FirstProduction_Date: string;
  SIW: string;
  Comment: string;
  Risk_Key: string;
};

// Custom hook for API client
export const useHistoryApiClient = () => {
  const { authData } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh((token: any | null, account: any | null) => {
    setIdToken(token)

  });
  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: APIV2,
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken ? `Bearer ${idToken}` : `Bearer ${authData.idToken}`,
      },
    });

    // Add response interceptor to handle 401 errors
    instance.interceptors.response.use(
      response => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const { token } = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here
            window.location.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );


    return instance;
  }, [idToken, authData.idToken]);


  const getVersionList = async (project_key: any) => {
    try {
      const response = await apiClient.get(`/snapshots/all/${project_key}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching version');
    }
  };


  const getHistoryist = async (version: any, project_key: any) => {
    try {
      const response = await apiClient.get(`/snapshots/compare/${version}/${project_key}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching history');
    }
  };

  const getArchieveList = async (project_key: any, page: any, limit: any) => {
    try {
      const response = await apiClient.get(`/snapshots/archives/${project_key}?page=${page}&limit=${limit}`);
      return response.data;
    } catch (err) {
      return handleError(err, "fetching archived change history");
    }
  };

  const restoreData = async (version: any, projectKey: any) => {
    try {
      const response = await apiClient.post(`snapshots/applySnapShot/${version}/${projectKey}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching list');
    }
  };



  //   // Milestone API
  //   const createMilestone = async (payload: MilestonePayload, project_key: string) => {
  //     try {
  //       const response = await apiClient.post(`/milestone/${project_key}`, payload);
  //       return response.data;
  //     } catch (err) {
  //       return handleError(err, 'creating milestone');
  //     }
  //   };


  return {
    getVersionList,
    getArchieveList,
    getHistoryist,
    restoreData
  };
};